import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate, fmtMoney, fmtErr, toastErr } from "../api";
import Layout from "../components/Layout";
import { ShareCard } from "../components/ShareCard";
import { useAuth } from "../AuthContext";
import { UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", address: "", email: "",
  billing_name: "", billing_company: "", billing_email: "", billing_cc_email: "", billing_phone: "",
  billing_address: "", billing_same_as_service: true, site_contact_name: "", site_contact_phone: "" };
const STATUS_STYLE = {
  new: "bg-secondary text-secondary-foreground",
  warm: "bg-[hsl(196,72%,56%)]/15 text-[hsl(196,72%,46%)]",
  converted: "bg-primary/10 text-primary",
  lost: "bg-muted text-muted-foreground",
};

export default function Leads() {
  const { user } = useAuth();
  const isOp = user?.role === "operator";
  const nav = useNavigate();
  const [leads, setLeads] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [convertFor, setConvertFor] = useState(null);
  const [convCustFor, setConvCustFor] = useState(null);
  const [inviteWanted, setInviteWanted] = useState(false);
  const [rev, setRev] = useState("");
  const [linkOwner, setLinkOwner] = useState("");
  const [owners, setOwners] = useState([]);

  const load = useCallback(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch((e) => toastErr(e));
    api.get("/leads/summary").then((r) => setSummary(r.data)).catch(() => {});
    if (isOp) api.get("/owners").then((r) => setOwners(r.data)).catch(() => {});
  }, [isOp]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", form);
      toast.success("Lead added — nice work");
      setForm(EMPTY); setShowAdd(false); load();
    } catch (e2) { toastErr(e2); }
  };

  const setStatus = async (l, status, extra = {}) => {
    try {
      await api.patch(`/leads/${l.id}`, { status, ...extra });
      toast.success(status === "warm" ? "Marked warm — bonus locked in" : status === "converted" ? "Converted — commission applies" : "Updated");
      setConvertFor(null); setRev(""); setLinkOwner("");
      load();
    } catch (e2) { toastErr(e2); }
  };

  const payout = async (uid) => {
    try {
      const r = await api.post("/leads/payout", uid ? { user_id: uid } : {});
      const total = r.data.payouts.reduce((t, p) => t + p.commission, 0);
      toast.success(`Paid out ${fmtMoney(total)} — counters reset`);
      load();
    } catch (e2) { toastErr(e2); }
  };

  const convertCustomer = async (l) => {
    try {
      const r = await api.post(`/leads/${l.id}/convert`, { invite: inviteWanted });
      toast.success(r.data.invite_sent
        ? "Customer created — portal invite emailed"
        : "Customer created (contact only — invite them later from their page)");
      setConvCustFor(null); setInviteWanted(false);
      nav(`/owners/${r.data.owner_id}`);
    } catch (e2) { toastErr(e2); }
  };

  const mine = summary?.mine;
  return (
    <Layout title="Leads">
      <ShareCard />
      {mine && (
        <div className="card-flat mb-4 p-4" data-testid="lead-mine">
          <p className="label-caps">Your current period</p>
          <div className="mt-2 grid grid-cols-4 gap-2 text-center">
            <div><p className="font-heading text-2xl font-bold" data-testid="mine-count">{mine.count}</p><p className="text-xs text-muted-foreground">leads</p></div>
            <div><p className="font-heading text-2xl font-bold" data-testid="mine-warm">{mine.warm}</p><p className="text-xs text-muted-foreground">warm</p></div>
            <div><p className="font-heading text-2xl font-bold" data-testid="mine-converted">{mine.converted}</p><p className="text-xs text-muted-foreground">converted</p></div>
            <div><p className="font-heading text-2xl font-bold text-primary" data-testid="mine-commission">{fmtMoney(mine.commission)}</p><p className="text-xs text-muted-foreground">earned</p></div>
          </div>
          {summary?.params && (
            <p className="mt-2 text-xs text-muted-foreground">
              {fmtMoney(summary.params.warm_bonus)} per warm lead · {summary.params.convert_pct}% of revenue at {fmtMoney(summary.params.convert_threshold)}+
            </p>
          )}
        </div>
      )}

      <button data-testid="add-lead-btn" onClick={() => setShowAdd(!showAdd)}
        className="mb-4 flex h-14 w-full items-center justify-center gap-2 rounded-md bg-primary font-bold text-primary-foreground">
        <UserPlus size={20} weight="bold" /> Add a lead
      </button>
      {showAdd && (
        <form onSubmit={add} className="card-flat mb-4 space-y-3 p-4" data-testid="add-lead-form">
          <input data-testid="lead-name" className="field-input w-full" placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <input data-testid="lead-phone" type="tel" className="field-input w-full" placeholder="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
          <input data-testid="lead-address" className="field-input w-full" placeholder="Address (optional)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <input data-testid="lead-email" type="email" className="field-input w-full" placeholder="Email (optional — auto-links if they become a customer)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <p className="pt-1 text-xs font-bold text-muted-foreground">Billing contact (if different from the contact above)</p>
          <input data-testid="lead-billing-name" className="field-input w-full" placeholder="Billing contact name" value={form.billing_name} onChange={(e) => setForm({ ...form, billing_name: e.target.value })} />
          <input data-testid="lead-billing-company" className="field-input w-full" placeholder="Company / organization (optional)" value={form.billing_company} onChange={(e) => setForm({ ...form, billing_company: e.target.value })} />
          <div className="flex gap-2">
            <input data-testid="lead-billing-email" type="email" className="field-input w-full" placeholder="Billing email" value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} />
            <input data-testid="lead-billing-cc" type="email" className="field-input w-full" placeholder="CC email (optional)" value={form.billing_cc_email} onChange={(e) => setForm({ ...form, billing_cc_email: e.target.value })} />
          </div>
          <input data-testid="lead-billing-phone" type="tel" className="field-input w-full" placeholder="Billing phone" value={form.billing_phone} onChange={(e) => setForm({ ...form, billing_phone: e.target.value })} />
          <label className="flex items-center gap-2 text-xs font-semibold">
            <input type="checkbox" data-testid="lead-billing-same" checked={form.billing_same_as_service}
              onChange={(e) => setForm({ ...form, billing_same_as_service: e.target.checked })} />
            Billing address same as service address
          </label>
          {!form.billing_same_as_service && (
            <input data-testid="lead-billing-address" className="field-input w-full" placeholder="Billing address" value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} />
          )}
          <p className="pt-1 text-xs font-bold text-muted-foreground">Site contact at the property (optional — e.g. tenant or on-site staff)</p>
          <div className="flex gap-2">
            <input data-testid="lead-site-contact-name" className="field-input w-full" placeholder="Name" value={form.site_contact_name} onChange={(e) => setForm({ ...form, site_contact_name: e.target.value })} />
            <input data-testid="lead-site-contact-phone" type="tel" className="field-input w-full" placeholder="Phone" value={form.site_contact_phone} onChange={(e) => setForm({ ...form, site_contact_phone: e.target.value })} />
          </div>
          <button data-testid="lead-save" className="btn-primary-big !h-14 !text-base">Save lead</button>
        </form>
      )}

      {isOp && summary?.team?.length > 0 && (
        <section className="mb-6" data-testid="lead-team">
          <div className="mb-2 flex items-center justify-between">
            <p className="label-caps">Team commissions</p>
            <button data-testid="lead-payout-all" onClick={() => payout(null)} className="text-xs font-bold text-primary underline">Pay out everyone</button>
          </div>
          <div className="space-y-2">
            {summary.team.map((p) => (
              <div key={p.user_id} className="card-flat flex items-center justify-between p-3" data-testid={`lead-team-${p.user_id}`}>
                <div>
                  <p className="text-sm font-semibold">{p.name} <span className="text-xs font-normal text-muted-foreground">({p.role === "subcontractor" ? "crew" : p.role})</span></p>
                  <p className="text-xs text-muted-foreground">{p.current.count} leads · {p.current.warm} warm · {p.current.converted} converted · {p.alltime} all-time</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-heading text-lg font-bold text-primary" data-testid={`lead-owed-${p.user_id}`}>{fmtMoney(p.current.commission)}</span>
                  <button data-testid={`lead-payout-${p.user_id}`} onClick={() => payout(p.user_id)} disabled={p.current.count === 0}
                    className="h-10 rounded-md border border-primary px-3 text-xs font-bold text-primary disabled:opacity-40">Paid</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="space-y-2" data-testid="lead-list">
        {!leads && <p className="text-muted-foreground">Loading…</p>}
        {leads?.length === 0 && <p className="card-flat p-6 text-sm text-muted-foreground">No leads yet — add one above when a neighbor or passerby asks about service.</p>}
        {leads?.map((l) => (
          <div key={l.id} className="card-flat space-y-2 p-4" data-testid={`lead-card-${l.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-semibold">{l.name}</p>
                <a href={`tel:${l.phone}`} className="text-sm font-semibold text-primary">{l.phone}</a>
                {l.address && <p className="text-xs text-muted-foreground">{l.address}</p>}
                {l.email && <p className="text-xs text-muted-foreground">{l.email}</p>}
              </div>
              <span data-testid={`lead-status-${l.id}`} className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_STYLE[l.status]}`}>{l.status}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {isOp && <>by <span className="font-semibold">{l.source_name}</span> · </>}{fmtDate(l.created_at)}
              {l.linked_owner_id && " · linked to owner account"}
              {l.payout_id && " · paid out"}
              {l.commission > 0 && <span className="font-bold text-primary"> · {fmtMoney(l.commission)}</span>}
              {l.status === "converted" && l.revenue > 0 && ` · revenue ${fmtMoney(l.revenue)}`}
            </p>
            {isOp && !l.payout_id && (
              <div className="flex flex-wrap gap-2">
                {l.status === "new" && (
                  <button data-testid={`lead-warm-${l.id}`} onClick={() => setStatus(l, "warm")}
                    className="h-10 rounded-md border border-border px-3 text-xs font-bold">Mark warm</button>
                )}
                <a data-testid={`lead-send-quote-${l.id}`} href={`/quotes/new?lead=${l.id}`}
                  className="flex h-10 items-center rounded-md border border-primary px-3 text-xs font-bold text-primary">Send quote</a>
                {["new", "warm"].includes(l.status) && (
                  <button data-testid={`lead-convert-customer-${l.id}`} onClick={() => { setConvCustFor(convCustFor === l.id ? null : l.id); setInviteWanted(false); }}
                    className="h-10 rounded-md bg-primary px-3 text-xs font-bold text-primary-foreground">Convert to customer</button>
                )}
                {["new", "warm"].includes(l.status) && (
                  <button data-testid={`lead-convert-${l.id}`} onClick={() => { setConvertFor(convertFor === l.id ? null : l.id); setRev(""); setLinkOwner(l.linked_owner_id || ""); }}
                    className="h-10 rounded-md border border-primary px-3 text-xs font-bold text-primary">Converted…</button>
                )}
                {!["lost", "converted"].includes(l.status) && (
                  <button data-testid={`lead-lost-${l.id}`} onClick={() => setStatus(l, "lost")}
                    className="h-10 rounded-md border border-border px-3 text-xs font-bold text-muted-foreground">Lost</button>
                )}
              </div>
            )}
            {isOp && convCustFor === l.id && (
              <div className="space-y-2 rounded-md border-2 border-primary p-3" data-testid={`lead-convert-cust-form-${l.id}`}>
                <p className="text-xs text-muted-foreground">
                  Creates <strong>{l.name}</strong> as a customer{l.address ? ` with the property "${l.address}"` : ""} and marks the lead converted.
                </p>
                <label className="flex items-center gap-2 text-xs font-semibold">
                  <input type="checkbox" data-testid={`convert-invite-toggle-${l.id}`} checked={inviteWanted}
                    disabled={!l.email} onChange={(e) => setInviteWanted(e.target.checked)} />
                  Invite to owner portal{l.email ? ` (${l.email})` : " — add an email to the lead first"}
                </label>
                <button data-testid={`convert-customer-confirm-${l.id}`} onClick={() => convertCustomer(l)}
                  className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Create customer</button>
              </div>
            )}
            {isOp && convertFor === l.id && (
              <div className="space-y-2 rounded-md border border-border p-3" data-testid={`lead-convert-form-${l.id}`}>
                <select data-testid={`lead-owner-${l.id}`} className="field-input w-full" value={linkOwner} onChange={(e) => setLinkOwner(e.target.value)}>
                  <option value="">Link owner account (optional)</option>
                  {owners.map((o) => <option key={o.id} value={o.id}>{`${o.name} — ${o.email}`}</option>)}
                </select>
                <input data-testid={`lead-revenue-${l.id}`} type="number" min="0" className="field-input w-full"
                  placeholder="Revenue $ (blank = auto from linked owner's invoices)" value={rev} onChange={(e) => setRev(e.target.value)} />
                <button data-testid={`lead-convert-save-${l.id}`}
                  onClick={() => setStatus(l, "converted", { ...(rev ? { revenue: Number(rev) } : {}), ...(linkOwner ? { linked_owner_id: linkOwner } : {}) })}
                  className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Confirm conversion</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {isOp && summary?.payouts?.length > 0 && (
        <section className="mt-6" data-testid="lead-payout-history">
          <p className="label-caps mb-2">Payout history</p>
          <div className="space-y-1">
            {summary.payouts.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-md border border-border p-2 text-xs" data-testid={`payout-${p.id}`}>
                <span>{p.user_name} — {p.count} leads</span>
                <span className="font-bold">{fmtMoney(p.commission)} · {fmtDate(p.created_at)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
}
